// src/App.tsx
import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import "./styles/ui/toaster.css";
import "./styles/ui/loadingspinner.css";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Layout from "./components/Layout/Layout";

// UI
import LoadingSpinner from "./components/ui/LoadingSpinner";
import { NotificationProvider } from "./contexts/NotificationContext";

const LOADING_LABELS: Record<string, string> = {
  "/": "dashboard",
  "/login": "inicio de sesión",
  "/recuperar-contrasena": "recuperación de contraseña",
  "/restablecer-contrasena": "cambio de contraseña",
  "/notificaciones": "notificaciones",
  "/clientes": "clientes",
  "/suscripciones": "suscripciones",
  "/onboarding": "onboarding",
  "/soporte": "soporte",
  "/analitica": "analítica",
  "/contabilidad": "contabilidad",
  "/configuracion/general": "configuración general",
  "/configuracion/comercial": "configuración comercial",
  "/configuracion/global": "configuración global",
  "/configuracion/seguridad": "configuración de seguridad",
  "/auditoria": "auditoría",
  "/plataforma/estado": "estado de plataforma",
  "/plataforma/mantenimiento": "mantenimiento de plataforma",
  "/plataforma/logs": "registros de plataforma",
  "/backup": "respaldos",
  "/informacion": "información del sistema",
  "/perfil": "perfil",
};

const getRouteLoadingMessage = (pathname: string) =>
  `Cargando ${LOADING_LABELS[pathname] || "pantalla"}...`;

// Cada módulo se descarga cuando el usuario entra a su ruta. De este modo, la
// pantalla inicial no incorpora el código de todo el panel administrativo.
const Login = lazy(() => import("./pages/auth/Login"));
const DashboardWrapper = lazy(
  () => import("./components/Dashboard/DashboardWrapper"),
);
const NotFound = lazy(() => import("./pages/NotFound"));
const Clientes = lazy(() => import("./pages/clientes/Clientes"));
const GestionPlanes = lazy(() => import("./pages/clientes/GestionPlanes"));
const Onboarding = lazy(() => import("./pages/clientes/Onboarding"));
const Soporte = lazy(() => import("./pages/soporte/Soporte"));
const Analitica = lazy(() => import("./pages/analitica/Analitica"));
const Contabilidad = lazy(() => import("./pages/soporte/Contabilidad"));
const ConfiguracionIndex = lazy(
  () => import("./pages/configuracion/ConfiguracionIndex"),
);
const Auditoria = lazy(() => import("./pages/auditoria/Auditoria"));
const Backup = lazy(() => import("./pages/plataforma/Backup"));
const Informacion = lazy(() => import("./pages/plataforma/Informacion"));
const LogsPlataforma = lazy(
  () => import("./pages/plataforma/LogsPlataforma"),
);
const EstadoPlataforma = lazy(
  () => import("./pages/plataforma/EstadoPlataforma"),
);
const MantenimientoPlataforma = lazy(
  () => import("./pages/plataforma/MantenimientoPlataforma"),
);
const Perfil = lazy(() => import("./pages/auth/Perfil"));
const NotificacionesPage = lazy(
  () => import("./pages/notificaciones/NotificacionesPage"),
);
const RecuperarContrasena = lazy(
  () => import("./pages/auth/RecuperarContrasena"),
);
const RestablecerContrasena = lazy(
  () => import("./pages/auth/RestablecerContrasena"),
);

// ============================================================
// ✅ COMPONENTE PROTECTED ROUTE CON PERMISOS
// ============================================================

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  permission?: string;
  anyPermissions?: string[];
  allPermissions?: string[];
  fallbackPath?: string;
}> = ({
  children,
  permission,
  anyPermissions = [],
  allPermissions = [],
  fallbackPath = "/",
}) => {
  const { pathname } = useLocation();
  const {
    isAuthenticated,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  } = useAuth();

  if (loading) {
    return (
      <LoadingSpinner message={getRouteLoadingMessage(pathname)} fullScreen={true} />
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // ✅ VERIFICAR PERMISOS
  if (permission && !hasPermission(permission)) {
    return <Navigate to={fallbackPath} />;
  }
  if (anyPermissions.length > 0 && !hasAnyPermission(anyPermissions)) {
    return <Navigate to={fallbackPath} />;
  }
  if (allPermissions.length > 0 && !hasAllPermissions(allPermissions)) {
    return <Navigate to={fallbackPath} />;
  }

  return <>{children}</>;
};

// ============================================================
// ✅ COMPONENTE PRINCIPAL DE RUTAS
// ============================================================

const AppRoutes: React.FC = () => {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const loadingMessage = getRouteLoadingMessage(pathname);

  return (
    <>
      <Toaster
        position="top-right"
        richColors
        closeButton={false}
        expand
        gap={12}
        theme={theme}
        toastOptions={{
          className: "my-custom-toast",
          duration: 2000,
        }}
      />

      <Suspense
        fallback={
          <LoadingSpinner message={loadingMessage} fullScreen={true} />
        }
      >
        <Routes>
        {/* ========== RUTAS PÚBLICAS ========== */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" /> : <Login />}
        />
        <Route path="/recuperar-contrasena" element={<RecuperarContrasena />} />
        <Route
          path="/restablecer-contrasena"
          element={<RestablecerContrasena />}
        />

        {/* ========== NOTIFICACIONES ========== */}
        <Route
          path="/notificaciones"
          element={
            <ProtectedRoute permission="notifications_view">
              <Layout>
                <NotificacionesPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== DASHBOARD ========== */}
        <Route
          path="/"
          element={
            <ProtectedRoute permission="dashboard_access">
              <Layout>
                <DashboardWrapper />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== CLIENTES ========== */}
        <Route
          path="/clientes"
          element={
            <ProtectedRoute permission="clients_access">
              <Layout>
                <Clientes />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== SUSCRIPCIONES ========== */}
        <Route
          path="/suscripciones"
          element={
            <ProtectedRoute permission="subscriptions_access">
              <Layout>
                <GestionPlanes />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== ONBOARDING ========== */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute permission="onboarding_access">
              <Layout>
                <Onboarding />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== SOPORTE ========== */}
        <Route
          path="/soporte"
          element={
            <ProtectedRoute permission="support_access">
              <Layout>
                <Soporte />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== ANALÍTICA ========== */}
        <Route
          path="/analitica"
          element={
            <ProtectedRoute permission="analytics_access">
              <Layout>
                <Analitica />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== CONTABILIDAD ========== */}
        <Route
          path="/contabilidad"
          element={
            <ProtectedRoute permission="analytics_access">
              <Layout>
                <Contabilidad />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== CONFIGURACIÓN - GENERAL ========== */}
        <Route
          path="/configuracion/general"
          element={
            <ProtectedRoute permission="general_config_access">
              <Layout>
                <ConfiguracionIndex tab="general" />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== CONFIGURACIÓN - COMERCIAL ========== */}
        <Route
          path="/configuracion/comercial"
          element={
            <ProtectedRoute permission="commercial_config_access">
              <Layout>
                <ConfiguracionIndex tab="comercial" />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== CONFIGURACIÓN - GLOBAL ========== */}
        <Route
          path="/configuracion/global"
          element={
            <ProtectedRoute permission="global_config_access">
              <Layout>
                <ConfiguracionIndex tab="global" />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== CONFIGURACIÓN - SEGURIDAD ========== */}
        <Route
          path="/configuracion/seguridad"
          element={
            <ProtectedRoute permission="security_config_access">
              <Layout>
                <ConfiguracionIndex tab="seguridad" />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== AUDITORÍA ========== */}
        <Route
          path="/auditoria"
          element={
            <ProtectedRoute permission="audit_access">
              <Layout>
                <Auditoria />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== INFRAESTRUCTURA ========== */}
        <Route
          path="/plataforma/estado"
          element={
            <ProtectedRoute permission="infrastructure_status_access">
              <Layout>
                <EstadoPlataforma />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/mantenimiento"
          element={
            <ProtectedRoute permission="infrastructure_maintenance_access">
              <Layout>
                <MantenimientoPlataforma />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/logs"
          element={
            <ProtectedRoute permission="infrastructure_logs_access">
              <Layout>
                <LogsPlataforma />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== SISTEMA ========== */}
        <Route
          path="/backup"
          element={
            <ProtectedRoute>
              <Layout>
                <Backup />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/informacion"
          element={
            <ProtectedRoute>
              <Layout>
                <Informacion />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ========== PERFIL ========== */}
        <Route
          path="/perfil"
          element={
            <ProtectedRoute>
              <Layout>
                <Perfil />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

// ============================================================
// ✅ APP PRINCIPAL
// ============================================================

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <Router>
            <AppRoutes />
          </Router>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
